/**
 * SpreadsheetNote Component
 *
 * @component
 * @description This component represents a spreadsheet note and provides an interface to view, edit, and delete it.
 * It displays the note's content, author, creation date, and provides actions to edit or delete the note.
 * This is a placeholder component - actual implementation will be handled separately.
 *
 * @param {SpreadsheetNoteItemProps} props - The properties and event handlers required by the component.
 * @returns {JSX.Element} - A JSX element representing the SpreadsheetNote component.
 */

import { useMemo } from 'react';
import { Grid } from '@mui/system';
import { CustomContainer } from 'components/CustomContainer';
import { useDrawerContext } from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetNoteItemProps } from '../types';

const MAX_CONTAINER_WIDTH = 4000;
const SpreadsheetNote = ({
  note,
  workbook,
  onChange,
  parentContainerHeight,
  isAttributeMapperEnabled,
  cells,
  isXlsxPreview = false,
}: SpreadsheetNoteItemProps) => {
  const { isPopped } = useDrawerContext();
  const columnWidths = useMemo(() => {
    const defaultWidth = 175;
    const contentWidths = Object.values(note.initialObj?.content?.column_data ?? {}).map(
      column => column.column_width || defaultWidth
    );
    contentWidths.push(defaultWidth); // For row header
    if (isAttributeMapperEnabled) {
      contentWidths.unshift(defaultWidth);
    }
    return contentWidths;
  }, [note, isAttributeMapperEnabled]);

  const containerWidth = useMemo(() => {
    const applicationWidth = window.innerWidth;

    return Math.min(applicationWidth, MAX_CONTAINER_WIDTH);
  }, []);

  const virtualization = useMemo(
    () => ({
      rowHeight: 30,
      rowOverscanCount: 6,
      height: parentContainerHeight,
      width: containerWidth,
      columnWidths,
      columnOverscanCount: 4,
    }),
    [parentContainerHeight, columnWidths, containerWidth]
  );

  return (
    <CustomContainer data-testid={isXlsxPreview ? 'spreadsheet-note-xlsx-preview' : 'spreadsheet-note'}>
      {note.cells && cells && (
        <Grid py={3} pl={!isPopped && isXlsxPreview ? 8.5 : 1} pr={1} size={{ xs: 12 }}>
          <ScalarSpreadsheet
            onChange={onChange}
            sheet={note}
            {...note}
            workbook={workbook}
            showToolbar
            virtualization={virtualization}
            disablePerfectScrollbar
          />
        </Grid>
      )}
    </CustomContainer>
  );
};

export default SpreadsheetNote;
